import React from 'react';
import { Link } from 'react-router-dom';
import { BlogArticleLayout } from '../../components/blog/BlogArticleLayout';

const QUICK_ANSWER = "The price-to-earnings (P/E) ratio is a stock's share price divided by its earnings per share. It tells you how many dollars investors pay for $1 of a company's annual profit. A P/E of 20 means investors are paying $20 for $1 of yearly earnings. Lower can signal value; higher usually signals growth expectations.";

const FAQS = [
  { q: 'Is a low P/E always better?', a: 'No. A low P/E can mean a stock is undervalued, but it can also mean the market expects earnings to fall. Always check why the P/E is low before assuming it is a bargain.' },
  { q: 'What is a "good" P/E ratio?', a: 'There is no single number. The S&P 500 has averaged a P/E of roughly 15 to 20 over the long run. Tech and growth stocks often trade above 30, while banks and utilities often sit below 15.' },
  { q: 'What is the difference between trailing and forward P/E?', a: 'Trailing P/E uses the last 12 months of actual earnings. Forward P/E uses analyst estimates for the next 12 months. Forward P/E is more current but depends on forecasts that can be wrong.' },
  { q: 'Why does a company sometimes have no P/E ratio?', a: 'If a company has zero or negative earnings, the P/E ratio is undefined or negative and is usually shown as "N/A". This is common for early-stage growth companies.' },
  { q: 'Where can I find a P/E ratio?', a: 'Almost any stock quote page (Yahoo Finance, Google Finance, your broker) lists it next to the share price. In Wallstreetle puzzles you will see P/E in the Valuation clue card.' },
];

export default function WhatIsPeRatioPage() {
  return (
    <BlogArticleLayout
      slug="what-is-pe-ratio"
      title="What Is a P/E Ratio? (With Examples)"
      description="The price-to-earnings ratio explained simply: formula, examples, what counts as high or low, and how investors actually use it."
      keywords="P/E ratio, price to earnings ratio, what is PE ratio, PE ratio explained, how to calculate PE ratio, good PE ratio, trailing PE, forward PE, valuation ratios"
      quickAnswer={QUICK_ANSWER}
      quickAnswerText={QUICK_ANSWER}
      faqs={FAQS}
      related={['what-is-eps', 'how-to-value-a-stock', 'learn-to-invest']}
      ctaText="Spot the P/E in today's puzzle"
      ctaSubtext="Wallstreetle shows you a real company's P/E ratio in every Valuation clue. Practise reading it in context."
    >
      <section>
        <h2>What is the P/E ratio?</h2>
        <p>
          The P/E ratio compares a company's share price to its earnings per share (EPS).
          The formula is simple: <strong>P/E = Share Price ÷ Earnings Per Share</strong>.
        </p>
        <p>
          If a stock trades at $100 and earned $5 per share over the last year, its P/E is 20.
          That means buyers are paying $20 today for every $1 of profit the company made last year.
          The P/E is shorthand for "how expensive is this stock relative to what it actually earns?"
        </p>
      </section>

      <section>
        <h2>How do you calculate a P/E ratio?</h2>
        <p>
          You only need two numbers, both of which sit on every stock quote page:
        </p>
        <ul>
          <li><strong>Share price</strong> — the current market price of one share.</li>
          <li><strong>EPS</strong> — net income for the last 12 months divided by shares outstanding. See our guide to <Link to="/blog/what-is-eps">what EPS means</Link>.</li>
        </ul>
        <p>
          Worked example: Microsoft trades at $420. Its trailing 12-month EPS is roughly $12. P/E ≈ 420 ÷ 12 = 35.
          Investors are paying about $35 for $1 of last year's earnings — high, but reasonable for a growing software company.
        </p>
      </section>

      <section>
        <h2>What's a "good" P/E ratio?</h2>
        <p>
          "Good" depends on the sector and the growth profile. Some rough benchmarks:
        </p>
        <ul>
          <li><strong>Below 15</strong> — often value or slow-growing (banks, utilities, mature consumer brands).</li>
          <li><strong>15–25</strong> — broad market average; reasonable for steady companies.</li>
          <li><strong>25–50</strong> — typical for growth companies expected to expand earnings quickly.</li>
          <li><strong>Above 50</strong> — high expectations baked in. Justified only if growth actually shows up.</li>
        </ul>
        <p>
          Always compare a P/E to the company's own history and to its direct competitors, not to the market as a whole.
        </p>
      </section>

      <section>
        <h2>What are the limits of the P/E ratio?</h2>
        <p>
          The P/E is one number, not a verdict. Three things it does not capture:
        </p>
        <ul>
          <li><strong>Debt.</strong> A heavily indebted company can have a low P/E that looks cheap but carries hidden risk. See <Link to="/blog/debt-to-equity-ratio">debt-to-equity</Link>.</li>
          <li><strong>Cash flow.</strong> Earnings can be massaged by accounting choices. <Link to="/blog/what-is-free-cash-flow">Free cash flow</Link> is harder to fake.</li>
          <li><strong>Future growth.</strong> P/E uses last year's earnings; it does not see what is coming. The PEG ratio (P/E ÷ growth rate) tries to fix this.</li>
        </ul>
      </section>

      <section>
        <h2>How does P/E apply when picking stocks?</h2>
        <p>
          Most investors use P/E as a sanity check, not a buy/sell trigger. Pull up two competitors in the same sector,
          compare their P/Es, and ask why they differ. If one is cheaper, find the reason — slower growth, more risk, a recent stumble.
          That difference is usually where the real story lives.
        </p>
        <p>
          For a fuller workflow, see our guide on <Link to="/blog/how-to-value-a-stock">how to value a stock in 5 minutes</Link>.
        </p>
      </section>
    </BlogArticleLayout>
  );
}
